/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useMemo } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-business-days";
import DataTable from "react-data-table-component";
import { Helmet } from "react-helmet";
import { withRouter } from "react-router";
import { toast } from "react-toastify";
import { CircleCheck, Clock9, Plus, Share2, SlidersHorizontal } from "lucide-react";
import { humanDateFormat, queryDateFormat, ticketStatusKpiIcons } from "../../../config/constants";
import { BlurSpinner } from "../../components/BlurSpinner";
import Breadcrumb from "../../components/common/breadcrumb";
import { ShowSearchBar } from "../../components/SearchBar";
import { useSearchBar } from "../../components/SearchBar/context/SearchBarContext";
import { useAuth } from "../../context/AuthContext";
import useFetcher from "../../hooks/useFetcher";
import usePaging from "../../hooks/usePaging";
import modulos from "../../mocks/modulos";
import { fetchCasasCentrales } from "../../services/casaCentralServices";
import { fetchTreeMotivo } from "../../services/motivoServices";
import { fetchRegiones } from "../../services/regionServices";
import { fetchSucursales } from "../../services/sucursalServices";
import { downloadTickets, fetchPiAsignable, fetchPiDerivadores, fetchTicketingCategories, fetchTickets } from "../../services/ticketingServices";
import { retriveRegionSucursalOptions } from "../../utils/retriveRegionSucursalOptions";
import { retriveMotivoSubmotivoOptions } from "./retriebeMotivoSubmotivoOptions";
import { fetchAllSubclientWithTickets } from "../../services/subclientesServices";
import { getFaceIconNameAndColorByRating } from "../TicketDetail/Inbox/Tabs";
import { ButtonPrimary, CustomDropdown, TicketingExportButton, ToggleButton, ToggleOption, ToggleWrapper } from "../../ui/buttons";
import KpiCardNP from "../../components/kpiCard/KpiCardNP";
import { getColorByEstado } from "./utils";
import { ContentKpi, IconKpi, KPIcard } from "../../ui/kpis";
import { tableCustomStylesNP } from "../../ui/table";
import SelectV2 from "../../components/SelectV2";
import { CustomLabel } from "../../ui/labels";
import TicketingNewModal from "../TicketingNew/ticketingNewModal";
import DateRangeComponentV2 from "../../components/DateRangeV2";

const filtarMotivos = (motivos, type) => {
  const motivosToFilter = ["Administrativo", "Comercial", "Selección", "Soporte"];

  if (type === "pincc") {
    // Mostrar solo los motivos que están en el array por label
    return motivos.filter((motivo) => motivosToFilter.includes(motivo.label));
  }

  if (type === "pininterno") {
    // Mostrar todos los motivos menos los que están en el array por label
    return motivos.filter((motivo) => !motivosToFilter.includes(motivo.label));
  }

  return motivos;
};

const Ticketing = ({ history, location }) => {
  const { currentUser, haveSucursalesAccess } = useAuth();
  const isAdmin = currentUser.haveAdminAccess(modulos.TICKETING);
  const isAudit = currentUser.haveAuditAccess(modulos.TICKETING);
  const { searchBarText } = useSearchBar();
  const [modalNewTicket, setModalNewTicket] = useState(false);
  const [ticketingCategories, isLoadingTicketingCategories, errTicketingCategories] = useFetcher(fetchTicketingCategories, undefined, "fetchTicketingCategories");
  const [piAsignable, isLoadingPiAsignable] = useFetcher(fetchPiAsignable, undefined, "allfetchPiAsignable");
  const [piDerivadores, isLoadingPiDerivadores] = useFetcher(fetchPiDerivadores, undefined, "allfetchPiDerivadores");
  const [regiones, isLoadingRegiones] = useFetcher(fetchRegiones, undefined, "fetchRegiones");
  const [casasCentrales, isLoadingCasasCentrales] = useFetcher(fetchCasasCentrales, undefined, "fetchCasasCentrales");
  const [sucursales, isLoadingSucursales] = useFetcher(fetchSucursales, undefined, "fetchSucursales");
  const [motivosConfig, isLoadingMotivos] = useFetcher(fetchTreeMotivo, undefined, "fetchTreeMotivo");
  const pagingController = usePaging(fetchTickets);
  const statusStats = pagingController.statusStats ? pagingController.statusStats : [];
  const timeStats = pagingController.timeStats ? pagingController.timeStats : {};
  const totalStats = statusStats.reduce((total, stat) => total + parseInt(stat.count, 10), 0);
  const totalDerivados = statusStats.reduce((total, stat) => total + parseInt(stat.derivados, 10), 0);
  const totalSinLeer = statusStats.reduce((total, stat) => total + parseInt(stat.noleidos, 10), 0);
  const [alertarSinLeer, setAlertarSinLeer] = useState(true);
  const filterDerivados = pagingController.query.derivados === "1";
  const [checkedCerrados, setCheckedCerrados] = useState(false);
  const [filtersActivesList, setFiltersActivesList] = useState([]);
  const [activedFilters, setActivedFilters] = useState(0);
  const [filteredMotivos, setFilteredMotivos] = useState(null);

  // trae todos los subclientes que tienen tickets
  const [subclients, isLoadingSubclients] = useFetcher(fetchAllSubclientWithTickets, undefined, "fetchAllSubclientes");

  const downloadCSV = async () => {
    toast.success("Su solicitud de exportacion esta siendo procesada");
    const link = document.createElement("a");
    const csv = await downloadTickets(location.search);
    const url = window.URL.createObjectURL(csv);
    const filename = `Tickets-${moment().format(humanDateFormat)}.csv`;
    link.href = url;
    link.setAttribute("download", filename);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    pagingController.setFilterValue({ query: searchBarText });
  }, [searchBarText]);

  if (totalSinLeer > 0 && alertarSinLeer) {
    setAlertarSinLeer(false);
    toast.warning(`Tiene ${totalSinLeer} tickets con novedades`);
  }

  const conditionalCellStyles = [
    {
      when: (row) => !row.lecturas || row.lecturas.length === 0,
      style: {
        fontWeight: "800",
      },
    },
    {
      when: (row) => row.highlightSla1 === true,
      style: {
        color: "red",
      },
    },
  ];

  const helperRegionSucursal = retriveRegionSucursalOptions(
    pagingController.query.region,
    pagingController.query.sucursal,
    regiones,
    sucursales,
    casasCentrales,
    isLoadingRegiones || isLoadingCasasCentrales || isLoadingSucursales,
    pagingController.query.mode !== "my" && haveSucursalesAccess(modulos.TICKETING),
    currentUser.user
  );

  const helperMotivoSubmotivo = retriveMotivoSubmotivoOptions(pagingController.query.motivo, pagingController.query.submotivo, motivosConfig, isLoadingMotivos);

  useEffect(() => {
    if (pagingController && pagingController.query && (pagingController.query.pincc || pagingController.query.pininterno)) {
      setFilteredMotivos(filtarMotivos(helperMotivoSubmotivo.motivosOptions, pagingController.query.pincc ? "pincc" : "pininterno"));
    } else if (helperMotivoSubmotivo.motivosOptions && helperMotivoSubmotivo.motivosOptions.length > 0) {
      setFilteredMotivos(helperMotivoSubmotivo.motivosOptions);
    }
  }, [pagingController.query.pincc, pagingController.query.pininterno, pagingController.data]);

  const categoriesOptions = ticketingCategories.categoria
    ? ticketingCategories.categoria
        .map((categoria) => {
          return { label: categoria.text, value: categoria.id };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  const clientsOptions = subclients
    ? subclients
        .map((cliente) => {
          return { label: cliente, value: cliente };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  let piAsignableFilteres = piAsignable;
  if (helperRegionSucursal.selectedRegion || helperRegionSucursal.selectedSucursal) {
    piAsignableFilteres = piAsignable.filter((pi) => {
      const sucursalesPi = (pi.CasaCentral || []).concat(pi.SucursalesAsignadas || []);
      return (
        (!helperRegionSucursal.selectedRegion || sucursalesPi.map((sucursal) => sucursal.region2).includes(helperRegionSucursal.selectedRegion)) &&
        (!helperRegionSucursal.selectedSucursal || sucursalesPi.map((sucursal) => sucursal.itemId).includes(helperRegionSucursal.selectedSucursal))
      );
    });
  }

  const sucursalesOptions = helperRegionSucursal.casasAndSucusOptions.filter((sucursal) => {
    return sucursal.estado === 1;
  });

  const piAsignableOptions = piAsignableFilteres
    .map((pi) => {
      return { label: `${pi.titulo} ${pi.nombre}`, value: pi.itemId };
    })
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const piDerivadoresOptions = piDerivadores
    ? piDerivadores
        .map((pi) => {
          return { label: pi.nombreApellido, value: pi.itemId };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  const canalOptions = ticketingCategories.canal
    ? ticketingCategories.canal
        .map((canal) => {
          return { label: canal.text, value: canal.id };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  const prioridadOptions = ticketingCategories.prioridad
    ? ticketingCategories.prioridad.map((prior) => {
        return { label: prior.text, value: prior.id };
      })
    : [];

  if (activedFilters < 0) {
    setActivedFilters(0);
  }

  const params = location.search || new URLSearchParams();

  let { activeFilterKeys, suma } = useMemo(() => {
    const contienePublico = /categoria/.test(params);
    const contieneCanal = /canal/.test(params);
    const contieneMotivo = /(?<=\b(motivo=))([\d,]+)/.test(params);
    const contieneSubmotivo = /(?<=\b(submotivo=))([\d,]+)/.test(params);
    const contienePrioridad = /prioridad/.test(params);
    const contieneCliente = /cliente/.test(params);
    const contieneDerivadoPor = /derivadopor/.test(params);
    const contienePincc = /pincc/.test(params);
    const contienePininterno = /pininterno/.test(params);

    // Lista para almacenar las keys activas
    activeFilterKeys = [];

    // Contar los valores separados por coma dentro de cada sección
    let publicoValores = 0;
    let canalValores = 0;
    let motivoValores = 0;
    let submotivoValores = 0;
    let prioridadValores = 0;
    let clienteValores = 0;
    let derivadoporValores = 0;
    let pinccValores = 0;
    let pininternoValores = 0;

    if (contienePublico) {
      const publicoMatch = params.match(/categoria=([^&]+)/);
      if (publicoMatch) {
        publicoValores = publicoMatch[1].split("%2C").length;
        activeFilterKeys.push("categoria");
      }
    }

    if (contieneCanal) {
      const canalMatch = params.match(/canal=([^&]+)/);
      if (canalMatch) {
        canalValores = canalMatch[1].split("%2C").length;
        activeFilterKeys.push("canal");
      }
    }

    if (contieneMotivo) {
      const motivoMatch = params.match(/motivo=([^,&]+)/);
      if (motivoMatch) {
        motivoValores = motivoMatch[1].split("%2C").length;
        activeFilterKeys.push("motivo");
      }
    }

    if (contieneSubmotivo) {
      const submotivoMatch = params.match(/submotivo=([^,&]+)/);
      if (submotivoMatch) {
        submotivoValores = submotivoMatch[1].split("%2C").length;
        activeFilterKeys.push("submotivo");
      }
    }

    if (contienePrioridad) {
      const prioridadMatch = params.match(/prioridad=([^&]+)/);
      if (prioridadMatch) {
        prioridadValores = prioridadMatch[1].split("%2C").length;
        activeFilterKeys.push("prioridad");
      }
    }

    if (contieneCliente) {
      const clienteMatches = params.match(/cliente=([^&]+)/g);
      if (clienteMatches) {
        clienteValores += clienteMatches.length;
        activeFilterKeys.push("cliente");
      }
    }

    if (contieneDerivadoPor) {
      const derivadoporMatches = params.match(/derivadopor=([^&]+)/g);
      if (derivadoporMatches) {
        derivadoporValores += derivadoporMatches.length;
        activeFilterKeys.push("derivadopor");
      }
    }

    if (contienePincc) {
      const pinccMatch = params.match(/pincc=([^&]+)/);
      if (pinccMatch) {
        pinccValores = pinccMatch[1].split("%2C").length;
        activeFilterKeys.push("pincc");
      }
    }

    if (contienePininterno) {
      const pininternoMatch = params.match(/pininterno=([^&]+)/);
      if (pininternoMatch) {
        pininternoValores = pininternoMatch[1].split("%2C").length;
        activeFilterKeys.push("pininterno");
      }
    }

    suma = publicoValores + canalValores + motivoValores + submotivoValores + prioridadValores + clienteValores + derivadoporValores + pinccValores + pininternoValores;

    return { activeFilterKeys, suma };
  }, [params]);

  useEffect(() => {
    setFiltersActivesList(activeFilterKeys);
    setActivedFilters(suma);
  }, [activeFilterKeys, suma]);

  const handleReactSelectChange = (selected, options) => {
    if ((options.action === "select-option" && options.option === undefined) || (options.action === "clear" && options.removedValues.length === 1)) {
      const newState = { [options.name]: selected ? selected.value : null };
      if (newState.region) {
        newState.sucursal = null;
      }
      pagingController.setFilterValue(newState);
    } else {
      const newState = { [options.name]: selected[0] ? selected.map((item) => item.value).join(",") : null };
      if (newState.region) {
        newState.sucursal = null;
      }
      pagingController.setFilterValue(newState);
    }
  };

  const handleMotivoSelectChange = (selected, options) => {
    // eslint-disable-next-line no-unused-expressions
    if ((options.action === "select-option" && options.option === undefined) || (options.action === "clear" && options.removedValues.length === 1)) {
      const newState = { [options.name]: selected ? selected.value : null };
      if (newState.region) {
        newState.sucursal = null;
      }
      pagingController.setFilterValue(newState);
    } else {
      const newState = { [options.name]: selected[0] ? selected.map((item) => item.value).join(",") : null };
      if (newState.region) {
        newState.sucursal = null;
      }
      pagingController.setFilterValue(newState);
    }
  };

  const handleFilterStatus = (status, newFilterDerivados = filterDerivados) => {
    const currentStatus = pagingController && pagingController.query.status && JSON.parse(pagingController.query.status);

    let newStatus = status;
    if (status === currentStatus) {
      newStatus = null;
    }
    setCheckedCerrados(false);
    pagingController.setFilterValue({ status: newStatus, derivados: newFilterDerivados ? "1" : undefined });
  };

  const handleVencidoFilter = () => {
    const { vencido } = pagingController.query;
    if (vencido) {
      pagingController.setFilterValue({ vencido: null });
    } else {
      pagingController.setFilterValue({ vencido: 1 });
    }
  };
  const handleCerradoFilter = () => {
    const { status } = pagingController.query;

    if (status === "3") {
      pagingController.setFilterValue({ status: null });
      setCheckedCerrados(false);
    } else {
      pagingController.setFilterValue({ status: "3" });
      setCheckedCerrados(true);
    }
  };

  const handleTicketClicked = (row) => {
    history.push({
      pathname: `/ticketing/${row.itemId}/detail/${location.search}`,
      state: { ticket: row, from: location },
    });
  };

  let title = "Tickets del Equipo";
  if (pagingController.query.mode === "my") {
    title = "Mis Tickets";
  } else if (pagingController.query.mode === "creador") {
    title = `Tickets Creados Para ${pagingController.query.creadoParaName || ""}`;
  } else if (pagingController.query.mode === "centralizer") {
    title = `Centralización`;
  }

  const reloadTickets = () => {
    setTimeout(() => {
      pagingController.reloadData(location);
    }, 1000);
  };

  return (
    <>
      <Helmet>
        <title>PIN - Sistema de Ticketing</title>
      </Helmet>
      <ShowSearchBar defaultValue={pagingController.query.query} />
      <Breadcrumb title={title} parent="Sistema de Ticketing" backButton={pagingController.query.mode === "creador"} />
      <BlurSpinner
        isLoading={
          // eslint-disable-next-line prettier/prettier
          pagingController.isLoading || isLoadingCasasCentrales || isLoadingRegiones || isLoadingSucursales || isLoadingTicketingCategories
        }
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <div className="row">
                <div className="col-sm-8 mb-3">
                  <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                    <CustomLabel htmlFor="region" fontSize="16px">
                      Region
                      <SelectV2
                        id="region"
                        name="region"
                        onChange={handleReactSelectChange}
                        value={
                          helperRegionSucursal.regionesOptions.find((option) => {
                            return option.value === helperRegionSucursal.selectedRegion;
                          }) || null
                        }
                        placeholder="Todo"
                        isClearable
                        isLoading={isLoadingRegiones}
                        isDisabled={isLoadingRegiones || helperRegionSucursal.disableRegionSucursal}
                        options={helperRegionSucursal.regionesOptions}
                      />
                    </CustomLabel>
                    <CustomLabel htmlFor="sucursal" fontSize="16px">
                      Sucursal
                      <SelectV2
                        isMulti
                        id="sucursal"
                        name="sucursal"
                        onChange={handleReactSelectChange}
                        value={
                          sucursalesOptions.filter((option) => (pagingController.query.sucursal ? pagingController.query.sucursal.includes(option.value) : "")) || null
                        }
                        placeholder="Todo"
                        isClearable
                        isLoading={isLoadingCasasCentrales || isLoadingSucursales}
                        isDisabled={isLoadingCasasCentrales || isLoadingSucursales || helperRegionSucursal.disableRegionSucursal}
                        options={sucursalesOptions}
                      />
                    </CustomLabel>
                    <CustomLabel htmlFor="asignado" fontSize="16px">
                      Asignado
                      <SelectV2
                        isMulti
                        id="asignado"
                        name="asignado"
                        onChange={handleReactSelectChange}
                        value={piAsignableOptions.filter((option) => (pagingController.query.asignado ? pagingController.query.asignado.includes(option.value) : ""))}
                        placeholder="Todos"
                        isClearable
                        isLoading={isLoadingPiAsignable}
                        disabled={isLoadingPiAsignable}
                        options={piAsignableOptions}
                      />
                    </CustomLabel>
                  </div>
                </div>
                <div className="d-flex col-sm-4 justify-content-end" style={{ gap: "10px" }}>
                  <CustomDropdown backgroundColor="#fff" box-shadow="0px 0px 4px 0px #00000040" width="45rem" padding="20px" toggleMaxWidth="130px">
                    <Dropdown>
                      <Dropdown.Toggle
                        className={`dropdown-toggle white-black px-2 flex align-items-center justify-content-between ${
                          activeFilterKeys && activeFilterKeys.length > 0 ? "filters-active" : ""
                        }`}
                        style={{ width: "200px", height: "40px" }}
                        id="dropdown-basic-filter"
                      >
                        <div className="d-flex align-items-center">
                          <SlidersHorizontal size={18} className="mr-2" />
                          Filtros {activeFilterKeys && activeFilterKeys.length > 0 && `(${activeFilterKeys.length})`}
                        </div>
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ backgroundColor: "#fff !important" }}>
                        <div className="d-flex flex-column" style={{ gap: "15px" }}>
                          {/* tags buttons */}
                          <div className="w-100 d-flex justify-content-between">
                            <ToggleWrapper minWidth="150px">
                              <ToggleOption
                                active={pagingController.query.pincc}
                                onClick={() => {
                                  if (pagingController.query.pincc) {
                                    pagingController.setFilterValue({ pincc: null });
                                  } else {
                                    pagingController.setFilterValue({ pincc: true, pininterno: null });
                                  }
                                }}
                              >
                                Pin CC
                              </ToggleOption>
                              <ToggleOption
                                active={pagingController.query.pininterno}
                                onClick={() => {
                                  if (pagingController.query.pininterno) {
                                    pagingController.setFilterValue({ pininterno: null });
                                  } else {
                                    pagingController.setFilterValue({ pininterno: true, pincc: null });
                                  }
                                }}
                              >
                                Pin Interno
                              </ToggleOption>
                            </ToggleWrapper>

                            {filtersActivesList.length > 0 ? (
                              <ToggleButton
                                onClick={() => {
                                  const filtersToReset = filtersActivesList.reduce((acc, key) => {
                                    acc[key] = null;
                                    return acc;
                                  }, {});

                                  pagingController.setFilterValue(filtersToReset);
                                }}
                              >
                                Borrar Filtros
                              </ToggleButton>
                            ) : (
                              <div />
                            )}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                              justifyItems: "stretch",
                              alignItems: "start",
                              gap: "1rem",
                            }}
                          >
                            {(isAdmin || isAudit) && (
                              <CustomLabel htmlFor="derivadopor" fontSize="18px" fontWeight="800">
                                Derivado Por
                                <SelectV2
                                  isMulti
                                  id="derivadopor"
                                  name="derivadopor"
                                  onChange={handleReactSelectChange}
                                  value={piDerivadoresOptions.filter((option) =>
                                    pagingController.query.derivadopor ? pagingController.query.derivadopor.includes(option.value) : ""
                                  )}
                                  placeholder="Todos"
                                  isClearable
                                  isLoading={isLoadingPiDerivadores}
                                  disabled={isLoadingPiDerivadores}
                                  options={piDerivadoresOptions}
                                />
                              </CustomLabel>
                            )}

                            <CustomLabel htmlFor="categoria" fontSize="18px" fontWeight="800">
                              Publico
                              <SelectV2
                                isMulti
                                id="categoria"
                                name="categoria"
                                onChange={handleReactSelectChange}
                                value={categoriesOptions.filter((option) =>
                                  pagingController.query.categoria ? pagingController.query.categoria.includes(option.value) : ""
                                )}
                                placeholder="Todo"
                                isClearable
                                isLoading={isLoadingTicketingCategories}
                                disabled={isLoadingTicketingCategories}
                                options={categoriesOptions}
                              />
                            </CustomLabel>

                            <CustomLabel htmlFor="subcategoria" fontSize="18px" fontWeight="800">
                              Cliente
                              <SelectV2
                                isMulti
                                id="cliente"
                                name="cliente"
                                className="w-100"
                                onChange={handleReactSelectChange}
                                value={clientsOptions.filter((option) => (pagingController.query.cliente ? pagingController.query.cliente.includes(option.value) : ""))}
                                placeholder="Todo"
                                isClearable
                                isLoading={isLoadingSubclients}
                                isDisabled={isLoadingSubclients}
                                options={clientsOptions}
                              />
                            </CustomLabel>

                            <CustomLabel htmlFor="canal" fontSize="18px" fontWeight="800">
                              Canal
                              <SelectV2
                                isMulti
                                id="canal"
                                name="canal"
                                className="w-100"
                                onChange={handleReactSelectChange}
                                value={canalOptions.filter((option) => (pagingController.query.canal ? pagingController.query.canal.includes(option.value) : ""))}
                                placeholder="Todo"
                                isClearable
                                isLoading={isLoadingTicketingCategories}
                                disabled={isLoadingTicketingCategories}
                                options={canalOptions}
                              />
                            </CustomLabel>

                            <CustomLabel htmlFor="categoria" fontSize="18px" fontWeight="800">
                              Motivo
                              <SelectV2
                                isMulti
                                id="motivo"
                                name="motivo"
                                className="w-100"
                                onChange={handleMotivoSelectChange}
                                value={helperMotivoSubmotivo.motivosOptions.filter((option) =>
                                  pagingController.query.motivo ? pagingController.query.motivo.includes(option.value) : ""
                                )}
                                placeholder="Todo"
                                isClearable
                                isLoading={isLoadingMotivos}
                                disabled={isLoadingMotivos}
                                options={!filteredMotivos ? helperMotivoSubmotivo.motivosOptions : filteredMotivos}
                              />
                            </CustomLabel>

                            <CustomLabel htmlFor="categoria" fontSize="18px" fontWeight="800">
                              Submotivo
                              <SelectV2
                                isMulti
                                id="submotivo"
                                name="submotivo"
                                className="w-100"
                                onChange={handleMotivoSelectChange}
                                value={helperMotivoSubmotivo.submotivosOptions.filter((option) =>
                                  pagingController.query.submotivo ? pagingController.query.submotivo.includes(option.value) : ""
                                )}
                                placeholder="Todo"
                                isClearable
                                isLoading={isLoadingMotivos}
                                disabled={isLoadingMotivos}
                                options={helperMotivoSubmotivo.submotivosOptions}
                              />
                            </CustomLabel>

                            <CustomLabel htmlFor="prioridad" fontSize="18px" fontWeight="800">
                              Prioridad
                              <SelectV2
                                isMulti
                                id="prioridad"
                                name="prioridad"
                                className="w-100"
                                onChange={handleReactSelectChange}
                                value={prioridadOptions.filter((option) =>
                                  pagingController.query.prioridad ? pagingController.query.prioridad.includes(option.value) : ""
                                )}
                                placeholder="Todo"
                                isClearable
                                isLoading={isLoadingTicketingCategories}
                                disabled={isLoadingTicketingCategories}
                                options={prioridadOptions}
                              />
                            </CustomLabel>
                            <CustomLabel htmlFor="fecha" fontSize="18px" fontWeight="800">
                              Rango de fechas
                              <DateRangeComponentV2 pagingController={pagingController} history={history} id="fecha" />
                            </CustomLabel>
                          </div>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </CustomDropdown>

                  {pagingController.query.mode === "my" && !isAudit && (
                    <ButtonPrimary type="button" onClick={() => setModalNewTicket(!modalNewTicket)}>
                      Nuevo ticket
                      <span className="ml-2 d-flex align-items-center">
                        <Plus size={16} strokeWidth={2.25} />
                      </span>
                    </ButtonPrimary>
                  )}
                  {modalNewTicket && <TicketingNewModal isOpen={modalNewTicket} toggle={() => setModalNewTicket(!modalNewTicket)} reloadTickets={reloadTickets} />}
                  {pagingController.query.mode === "team" && (
                    <ToggleWrapper>
                      <ToggleOption active={filterDerivados} onClick={() => handleFilterStatus(null, true)}>
                        <input type="radio" checked={filterDerivados} readOnly />
                        Ver derivados
                      </ToggleOption>
                      <ToggleOption active={!filterDerivados} onClick={() => handleFilterStatus(null, false)}>
                        <input type="radio" checked={!filterDerivados} readOnly />
                        Ver todos
                      </ToggleOption>
                    </ToggleWrapper>
                  )}
                </div>
              </div>
              {isLoadingTicketingCategories || errTicketingCategories ? (
                <></>
              ) : (
                <>
                  {/* ----------------- CARDS DE STATUS ---------------- */}
                  <div
                    className="d-flex flex-wrap mb-3"
                    style={{
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    {ticketingCategories && ticketingCategories.status && pagingController.statusStats
                      ? ticketingCategories.status
                          .reduce((acc, el) => {
                            // Elimina la cat cerrado si el switch esta off
                            if (!checkedCerrados && el.id !== 3) {
                              acc.push(el);
                            }
                            // Elimina la cat resuelto si el switch esta on
                            if (checkedCerrados && el.id !== 5) {
                              acc.push(el);
                            }
                            return acc;
                          }, [])
                          .map((estado) => {
                            const statusDef = {
                              id: estado.id,
                              text: estado.text,
                              color: getColorByEstado(estado.id),
                            };
                            const value = { count: pagingController.statusStats[estado.text] || 0 };
                            if (!ticketStatusKpiIcons[estado.id]) return null;
                            return (
                              ticketStatusKpiIcons[estado.id] && (
                                <div
                                  key={estado.id}
                                  style={{
                                    flex: "1 1 200px",
                                    maxWidth: "250px",
                                  }}
                                >
                                  <KpiCardNP
                                    statusDef={statusDef}
                                    icon={ticketStatusKpiIcons[estado.id]}
                                    value={value}
                                    total={filterDerivados ? totalDerivados : totalStats}
                                    onClick={handleFilterStatus}
                                    selected={pagingController.query.status ? pagingController.query.status.split(",").map(Number).includes(estado.id) : false}
                                  />
                                </div>
                              )
                            );
                          })
                      : null}
                  </div>

                  {/* -------------------------TARJETAS ALARGADAS KPI------------------------------ */}
                  <div className="d-flex w-100 mb-3" style={{ justifyContent: "start", gap: "8px" }}>
                    <KPIcard className="mb-0" borderRadius="4px" style={{ height: "42px" }}>
                      <div className="d-flex align-items-center justify-content-between px-3 w-100">
                        <IconKpi svgWidth="20px" svgHeight="20px">
                          <Clock9 color="#F0292A" strokeWidth={2.5} size={18} />
                        </IconKpi>
                        <div className="d-flex justify-content-between w-100 mx-2">
                          <ContentKpi className="flex-row">
                            <h4>Tiempo de respuesta</h4>
                          </ContentKpi>
                          <ContentKpi className="flex-row">
                            {timeStats && timeStats.respuesta_avg ? (
                              <>
                                <h4 className="pl-1" style={{ fontWeight: 700 }}>{`${parseFloat(timeStats.respuesta_avg).toFixed(2)} días`}</h4>
                              </>
                            ) : (
                              <h4>-</h4>
                            )}
                          </ContentKpi>
                        </div>
                      </div>
                    </KPIcard>

                    <KPIcard className="mb-0" borderRadius="4px" style={{ height: "42px" }}>
                      <div className="d-flex align-items-center justify-content-between px-3 w-100">
                        <IconKpi svgWidth="20px" svgHeight="20px">
                          <CircleCheck color="#F0292A" strokeWidth={2.5} size={18} />
                        </IconKpi>
                        <div className="d-flex justify-content-between w-100 mx-2">
                          <ContentKpi className="flex-row">
                            <h4>Tiempo de resolución</h4>
                          </ContentKpi>
                          <ContentKpi className="flex-row">
                            {timeStats && timeStats.resolucion_avg ? (
                              <>
                                <h4 className="pl-1" style={{ fontWeight: 700 }}>{`${parseFloat(timeStats.resolucion_avg).toFixed(2)} días`}</h4>
                              </>
                            ) : (
                              <h4>-</h4>
                            )}
                          </ContentKpi>
                        </div>
                      </div>
                    </KPIcard>
                  </div>
                </>
              )}
              <div className="table-responsive support-table border">
                <DataTable
                  title="Tickets"
                  paginationComponentOptions={{ rowsPerPageText: "Tickets por pagina", rangeSeparatorText: "De", selectAllRowsItemText: "Todos" }}
                  noDataComponent={
                    <div className="p-3">
                      <b>No se encontraron tickets</b>
                    </div>
                  }
                  striped
                  pagination
                  sortServer
                  paginationServer
                  actions={[
                    <ToggleButton onClick={handleVencidoFilter} isSelected={pagingController.query.vencido} marginRight="2px" padding="5px 10px">
                      Vencidos
                    </ToggleButton>,
                    <ToggleButton onClick={handleCerradoFilter} isSelected={pagingController.query.status === "3"} marginRight="2px" padding="5px 10px">
                      Cerrados
                    </ToggleButton>,
                    pagingController.totalRows > 0 && (
                      <TicketingExportButton type="button" aria-label="Download CSV" onClick={(e) => downloadCSV(e.target.value)}>
                        <Share2 color="#293134" size={20} />
                      </TicketingExportButton>
                    ),
                  ]}
                  onRowClicked={handleTicketClicked}
                  data={pagingController.data}
                  onSort={pagingController.handleSortsChange}
                  paginationTotalRows={pagingController.totalRows}
                  paginationDefaultPage={parseInt(pagingController.query.page || 1, 10)}
                  onChangeRowsPerPage={pagingController.handleRecordsPerPageChange}
                  paginationPerPage={parseInt(pagingController.query.perpage || 10, 10)}
                  onChangePage={pagingController.handlePageChange}
                  customStyles={tableCustomStylesNP}
                  columns={[
                    {
                      name: "N° caso",
                      selector: (row) => `${row.fechaDerivadoStart ? "D" : ""}${row.itemId}`,
                      sortable: true,
                      center: true,
                      id: "itemId",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                      },
                      conditionalCellStyles,
                    },
                    {
                      name: "Creado",
                      selector: (row) => (row.fechaCreacionStart ? moment.utc(row.fechaCreacionStart).format(humanDateFormat) : ""),
                      sortable: true,
                      center: true,
                      id: "fechaCreacion",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                        width: "115px",
                      },
                      width: "115px",
                      minWidth: "115px",
                      conditionalCellStyles,
                    },
                    {
                      name: "Vence",
                      selector: (row) => {
                        const initialDate = row.fechaCreacionStart ? moment(new Date(row.fechaCreacionStart)).format(queryDateFormat) : null;
                        if (row.prioridadCat && row.prioridadCat.text === "Alta") {
                          return row.grupoSla === 2
                            ? moment(initialDate).businessAdd(1).format(humanDateFormat)
                            : moment(initialDate).businessAdd(5).format(humanDateFormat);
                        }
                        if (row.prioridadCat && row.prioridadCat.text === "Media") {
                          return row.grupoSla === 2
                            ? moment(initialDate).businessAdd(2).format(humanDateFormat)
                            : moment(initialDate).businessAdd(7).format(humanDateFormat);
                        }
                        if (row.prioridadCat && row.prioridadCat.text === "Baja") {
                          return row.grupoSla === 2
                            ? moment(initialDate).businessAdd(3).format(humanDateFormat)
                            : moment(initialDate).businessAdd(10).format(humanDateFormat);
                        }
                        return "-";
                      },
                      sortable: true,
                      center: true,
                      wrap: true,
                      id: "vencimiento",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                        width: "115px",
                      },
                      minWidth: "115px",
                      conditionalCellStyles,
                    },
                    {
                      name: "Actualización",
                      selector: (row) => (row.updatedAt ? moment.utc(row.updatedAt).format(humanDateFormat) : ""),
                      sortable: true,
                      center: true,
                      id: "updatedAt",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                        width: "115px",
                      },
                      minWidth: "115px",
                      conditionalCellStyles,
                      width: "110px",
                    },
                    {
                      name: "Creado para",
                      selector: (row) =>
                        row.CreadoPara ? row.CreadoPara.nombreApellido || row.CreadoPara.razonSocial || `${row.CreadoPara.nombre} ${row.CreadoPara.apellido}` : "",
                      sortable: true,
                      center: true,
                      id: "creadoPara",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                      },
                      conditionalCellStyles,
                    },
                    {
                      name: "Asignado a",
                      selector: (row) => (row.PersonalInternoAsignado ? row.PersonalInternoAsignado.nombreApellido : ""),
                      sortable: true,
                      center: true,
                      wrap: true,
                      id: "personalInternoAsignado",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                        minWidth: "155px",
                      },
                      conditionalCellStyles,
                    },
                    {
                      name: "Sucursal",
                      selector: (row) => {
                        if (row.SucursaldepartamentoAsignadoCasaCentral) {
                          return row.SucursaldepartamentoAsignadoCasaCentral.title;
                        }
                        if (row.SucursaldepartamentoAsignadoSucursales) {
                          return row.SucursaldepartamentoAsignadoSucursales.titulo;
                        }
                        return "";
                      },
                      sortable: true,
                      center: true,
                      wrap: true,
                      id: "sucursaldepartamentoAsignado",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                      },
                      conditionalCellStyles,
                    },
                    {
                      name: "Estado",
                      selector: ({ status, statusCat, vencido }) =>
                        status ? (
                          <div className="text-center">
                            <span
                              className="badge"
                              style={{
                                fontSize: "unset",
                                background: `#${statusCat.color}`,
                              }}
                            >
                              {statusCat.text}
                              {vencido === 1 ? (
                                <>
                                  <br />
                                  (Vencido)
                                </>
                              ) : null}
                            </span>
                          </div>
                        ) : (
                          <></>
                        ),
                      sortable: true,
                      center: true,
                      id: "status",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                      },
                      conditionalCellStyles,
                    },
                    {
                      name: "Valoración",
                      selector: (row) => {
                        const { iconName, iconColor } = getFaceIconNameAndColorByRating(row.valoracion !== null ? row.valoracion : null);
                        return row.valoracion !== null ? (
                          <div>
                            <FontAwesomeIcon color={iconColor} size="2x" icon={iconName} />
                          </div>
                        ) : (
                          "-"
                        );
                      },
                      // sortable: true,
                      center: true,
                      id: "valoracion",
                      style: {
                        cursor: "pointer",
                        textAlign: "center",
                      },
                      width: "110px",
                      padding: "0px",
                      conditionalCellStyles,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </BlurSpinner>
    </>
  );
};

export default withRouter(Ticketing);
